import { useEffect, useRef } from "react";
import OpenSeadragonLib from "openseadragon";

const OpenSeadragon = OpenSeadragonLib?.default || OpenSeadragonLib;

export default function TiledOrthomosaicViewer({
  tileSource,
  visible = true,
  onReady,
  onError,
  onLoadStart,
  onFirstDraw,
}) {
  const hostRef = useRef(null);
  const viewerRef = useRef(null);
  // Callbacks are read through a ref so new inline handlers from the parent don't rebuild the viewer.
  const callbacksRef = useRef({});
  callbacksRef.current = { onReady, onError, onLoadStart, onFirstDraw };

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !tileSource) return undefined;

    const viewer = OpenSeadragon({
      element: host,
      prefixUrl: "",
      showNavigationControl: false,
      showNavigator: true,
      navigatorPosition: "BOTTOM_RIGHT",
      navigatorSizeRatio: 0.18,
      navigatorAutoFade: false,
      animationTime: 0.25,
      blendTime: 0.1,
      constrainDuringPan: true,
      visibilityRatio: 1,
      minZoomImageRatio: 1,
      maxZoomPixelRatio: 1.5,
      gestureSettingsMouse: {
        clickToZoom: false,
        dblClickToZoom: true,
        flickEnabled: true,
      },
      tileSources: tileSource,
    });

    viewerRef.current = viewer;
    callbacksRef.current.onLoadStart?.();
    viewer.addHandler("open", () => callbacksRef.current.onReady?.(viewer));
    viewer.addHandler("open-failed", () => callbacksRef.current.onError?.());
    // "tile-drawn" is not raised by the WebGL drawer; tile load events are drawer-independent.
    let firstTileSeen = false;
    const onFirstTile = () => {
      if (firstTileSeen) return;
      firstTileSeen = true;
      callbacksRef.current.onFirstDraw?.();
    };
    viewer.addHandler("tile-loaded", onFirstTile);
    viewer.addHandler("tile-load-failed", onFirstTile);

    return () => {
      viewer.destroy();
      viewerRef.current = null;
    };
  }, [tileSource]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer?.world?.getItemCount?.()) return;
    const item = viewer.world.getItemAt(0);
    item?.setOpacity(visible ? 1 : 0);
  }, [visible]);

  return <div className="sv-osd" ref={hostRef} />;
}

export function zoomTiledViewer(viewer, factor) {
  if (!viewer?.viewport || viewer.isOpen?.() === false) return;
  viewer.viewport.zoomBy(factor);
  viewer.viewport.applyConstraints();
}

export function fitTiledViewer(viewer) {
  if (!viewer?.viewport || viewer.isOpen?.() === false) return;
  viewer.viewport.goHome(true);
}

export function rotateTiledViewerHome(viewer) {
  if (!viewer?.viewport || viewer.isOpen?.() === false) return;
  viewer.viewport.setRotation(0, true);
  viewer.viewport.goHome(true);
}
